import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp } from 'lucide-react';
import { STATUS_COLORS } from './modeIcons';
import { VesselGlyph, ModeGlyph, SwatchStack } from './legendGlyphs';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';
import { useTheme } from '../../../context/ThemeContext';

const SURFACE = floatingSurfaceClass(94);

/** Neutral glyph colour — the legend keys shape, not status. */
const GLYPH = 'text-[#5D625F] dark:text-[#9AA09A]';
const ModeRow = ({ children, label, note }) => (
  <div className="flex items-center gap-[10px]">
    <span className={`flex h-[16px] w-[16px] shrink-0 items-center justify-center ${GLYPH}`}>{children}</span>
    <span className="font-['Inter'] text-[11.5px] whitespace-nowrap text-[#343440] dark:text-[#E6E8E6]">
      {label}
      {note ? <span className="text-[11px] text-[#828382]">{` · ${note}`}</span> : null}
    </span>
  </div>
);

/**
 * "Mode & status" key for the Shipments layer — what the marker shapes and
 * colours mean when shipments are the only thing on screen.
 *
 * All no longer uses this: with regions drawn too there are two things to
 * explain, and the design puts both in one panel (see MapLegend).
 *
 * The status dots read their fills from `STATUS_COLORS` — the same table the
 * markers colour themselves from — so the key can never describe a palette the
 * map isn't using.
 */
// At-port and discharged were dropped by the review. Their colours stay in
// modeIcons because rows still carry those states; they are simply no longer
// keyed here or offered in the status filter.
const STATUS_ROWS = ['on_time', 'ahead', 'delayed'];

const STATUS_FALLBACKS = {
  on_time: 'On time',
  ahead: 'Ahead',
  delayed: 'Delayed',
  at_port: 'At port',
  discharged: 'Discharged',
};

const MapModeStatusLegend = ({ className = '', defaultOpen = false }) => {
  // Closed by default on every layer: the map is the content, and a key
  // the user has not asked for should not cover it on arrival. The prop
  // stays so a caller can still open one deliberately.
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const title = t('map.legend.title', 'Mode & status');

  return (
    <div className={`flex w-[152px] flex-col items-stretch gap-[4px] ${className}`}>
      {open && (
        <div className={`rounded-[12px] border border-solid px-[12px] py-[12px] ${SURFACE}`}>
      <p className="font-['Inter'] text-[9.5px] font-extrabold uppercase tracking-[0.76px] text-[#9CA3AF] dark:text-[#6B716C]">
        {title}
      </p>

      <div className="mt-[9px] flex flex-col gap-[7px]">
        <ModeRow label={t('map.legend.vessel', 'Vessel')} note={t('map.legend.vesselNote', 'sea')}>
          <VesselGlyph className="h-[16px] w-[16px]" />
        </ModeRow>
        <ModeRow label={t('map.legend.truck', 'Truck')} note={t('map.legend.truckNote', 'inland')}>
          <ModeGlyph mode="truck" className="h-[16px] w-[16px]" />
        </ModeRow>
        <ModeRow label={t('map.legend.air', 'Air')}>
          <ModeGlyph mode="air" className="h-[16px] w-[16px]" />
        </ModeRow>
        <ModeRow label={t('map.legend.inPort', 'In port')} note={t('map.legend.inPortNote', 'waiting')}>
          <img
            src={isDark ? '/assets/icons/box-dark.svg' : '/assets/icons/box-light.svg'}
            alt=""
            aria-hidden="true"
            className="h-[16px] w-[16px]"
          />
        </ModeRow>
      </div>

      <div className="mt-[10px] h-px w-full bg-[#EEF0EC] dark:bg-[#2A302C]" />

      <div className="mt-[9px] flex flex-col gap-[7px]">
        {STATUS_ROWS.map((id) => (
          <div key={id} className="flex items-center gap-[9px]">
            <span
              className="h-[10px] w-[10px] shrink-0 rounded-full"
              style={{ backgroundColor: STATUS_COLORS[id] }}
            />
            <span className="font-['Inter'] text-[11px] font-medium whitespace-nowrap text-[#343440] dark:text-[#E6E8E6]">
              {t(`map.filters.shipmentStatus.${id}`, STATUS_FALLBACKS[id])}
            </span>
          </div>
        ))}
        </div>
        </div>
      )}

      {/* Toggle. Last in DOM order so it stays put as the panel opens upward. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex h-[28px] w-full items-center rounded-[14px] border border-solid border-[#E6E9E4] bg-white pl-[10px] pr-[8px] drop-shadow-[0px_1px_1.5px_rgba(17,24,39,0.07)] dark:border-[#2A302C] dark:bg-[#1B1F1C]"
      >
        <span className="font-['Inter'] text-[10px] font-semibold whitespace-nowrap text-[#343440] dark:text-[#E6E8E6]">
          {title}
        </span>
        <SwatchStack colors={STATUS_ROWS.map((id) => STATUS_COLORS[id])} />
        <ChevronUp
          size={10}
          className={`shrink-0 text-[#5D625F] transition-transform dark:text-[#9AA09A] ${open ? '' : 'rotate-180'}`}
        />
      </button>
    </div>
  );
};

export default MapModeStatusLegend;
